/**
 * User role management shapes, generated from OrbitalX.Dto — see api-generated.d.ts.
 *
 * Five unions are narrowed here (RoleType, RoleSetSource, InviteAccessState, InviteDeliveryState, ReInviteOutcome)
 * because the C# DTOs declare those properties `string`. Narrowing a leaf forces every type that
 * contains it to be re-declared too, which is what most of the length below is.
 */

import type {
  CompanyInviteStatusDto as GeneratedCompanyInviteStatusDto,
  InviteDeliveryDto as GeneratedInviteDeliveryDto,
  ReInviteUserResultDto as GeneratedReInviteUserResultDto,
  UserCompanyRoleSetViewDto as GeneratedUserCompanyRoleSetViewDto,
  UserInviteStatusDto as GeneratedUserInviteStatusDto,
  UserProfileCreationResultDto as GeneratedUserProfileCreationResultDto,
  UserProfileWithRolesDto as GeneratedUserProfileWithRolesDto,
  WorkspaceRolesViewDto as GeneratedWorkspaceRolesViewDto,
} from './api-generated'

export type {
  InviteEmailResultDto,
  UserCompanyRoleSetUpdateDto,
  UserProfileCreateDto,
  UserRoleAssignmentDto,
  WorkspaceRolesCreateDto,
  WorkspaceRolesUpdateDto,
} from './api-generated'

export type RoleType = 'Viewer' | 'Editor' | 'Admin'

/**
 * `CanAdmin` / `CanEdit` / `CanView` are the EFFECTIVE capabilities: a company-level role grants them
 * in every workspace, so they are not derivable from `Roles` alone.
 */
export interface WorkspaceRolesViewDto extends Omit<GeneratedWorkspaceRolesViewDto, 'Roles'> {
  Roles: RoleType[]
}

/**
 * `AllCustomers` access was written by the managing company's "Access to all customers" switch
 * (ORB-1019). Only the switch changes it, so a customer's Users page locks the row.
 */
export type RoleSetSource = 'Manual' | 'AllCustomers'

/**
 * `Id` is `{UserProfileId}:{ClientCompanyId}`. `AllCustomersRole` is set only on a person's
 * home-company role set, and is null while their switch is off.
 */
export interface UserCompanyRoleSetViewDto
  extends Omit<GeneratedUserCompanyRoleSetViewDto, 'CompanyRoles' | 'WorkspaceRoles' | 'Source' | 'AllCustomersRole'> {
  CompanyRoles: RoleType[]
  WorkspaceRoles: WorkspaceRolesViewDto[]
  Source: RoleSetSource
  AllCustomersRole?: RoleType | null
}

/** `UserCompanyRoleSet` is null when the user has no role assignments in this company. */
export interface UserProfileWithRolesDto
  extends Omit<GeneratedUserProfileWithRolesDto, 'UserCompanyRoleSet'> {
  UserCompanyRoleSet?: UserCompanyRoleSetViewDto | null
}

/**
 * ⚑ Ordinals, not names.
 *
 * `UserProfileCreationResultDto.Status` is a C# enum PROPERTY, and the worker serializer installs no
 * JsonStringEnumConverter, so it arrives as an integer. This was previously declared here as a
 * STRING enum ('Created', …) and switched on as one, which meant no case ever matched and the
 * post-invite toast never showed. Same name, corrected values: the declaration order in
 * OrbitalX.Dto/User/UserProfileCreationResultDto.cs, which assigns none explicitly.
 *
 * The generated file also exports a `UserProfileCreationStatus` union of the member NAMES. That one
 * describes the other enum path — a `string` property a mapper filled with ToDescription() — and no
 * endpoint puts these names on the wire. This is the type to import.
 */
export enum UserProfileCreationStatus {
  /** A new user profile was created and invited to the company. */
  Created = 0,
  /** An existing user from the managing company was given access to this company. */
  ExistingUserAdded = 1,
  /** A soft-deleted user profile was reactivated. */
  Reactivated = 2,
}

/**
 * `InviteEmail` is independent of `Status`: the user, their roles and their Auth0 invitation are all
 * committed before the email is attempted, so a failed email is not a failed creation. It is null
 * when no email was due — somebody added from a managing company already has an account.
 */
export interface UserProfileCreationResultDto
  extends Omit<GeneratedUserProfileCreationResultDto, 'UserProfileWithRoles'> {
  UserProfileWithRoles: UserProfileWithRolesDto
}

/** Whether somebody can get into the product, from Auth0's membership and invitation lists. */
export type InviteAccessState =
  | 'Active'
  | 'Invited'
  | 'InviteExpired'
  | 'NoInvitationOnRecord'
  | 'SignedInNotAMember'
  | 'NoInviteRequired'
  | 'StatusUnavailable'

/** What became of the invitation email, from our own send record. Auth0 knows none of this. */
export type InviteDeliveryState =
  | 'Delivered'
  | 'Bounced'
  | 'Suppressed'
  | 'BlockedByEnvironmentGuard'
  | 'Queued'
  | 'NeverSent'
  | 'Unknown'

/**
 * `RfcMessageId` is the RFC 5322 Message-ID, so a recipient's own mail administrator can search their
 * logs for the exact message. Null on anything that never dispatched, which is most of a stuck
 * population.
 */
export interface InviteDeliveryDto extends Omit<GeneratedInviteDeliveryDto, 'State'> {
  State: InviteDeliveryState
}

/** `HomeCompanyName` is set when this person belongs to another company and reaches this one
 *  through a role set. */
export interface UserInviteStatusDto
  extends Omit<GeneratedUserInviteStatusDto, 'AccessState' | 'Delivery'> {
  AccessState: InviteAccessState
  Delivery?: InviteDeliveryDto | null
}

/**
 * `Auth0Available` false means every access state reads as unavailable rather than guessing.
 * `EmailDeliveryAvailable` is independent of it. `Auth0ScopeMissing` means Auth0 refused for want of
 * a scope — a configuration error, not an outage.
 */
export interface CompanyInviteStatusDto extends Omit<GeneratedCompanyInviteStatusDto, 'Items'> {
  Items: UserInviteStatusDto[]
}

export type ReInviteOutcome = 'ReInvited' | 'Cooldown' | 'NoInviteRequired'

/**
 * `ReInvited` is true only when the previous invitations were revoked and a new one minted — false
 * for every refusal, and independent of whether the email then went out. `Reason` explains a refusal,
 * `CooldownSecondsRemaining` is set only on Cooldown, and `HomeCompanyName` only on NoInviteRequired.
 */
export interface ReInviteUserResultDto extends Omit<GeneratedReInviteUserResultDto, 'Outcome'> {
  Outcome: ReInviteOutcome
}
